import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLiveJob } from '@/hooks/useLiveUpdates';
import { formatNaira } from '@/lib/utils';
import { CaretLeft, MapPin, Clock, Star, Check, PaperPlaneTilt, ChatCircle, CurrencyDollar, Camera, WarningOctagon, ArrowsCounterClockwise, Image, ListChecks, Path, Lightning } from '@phosphor-icons/react';

export default function JobDetailPage() {
  const { id } = useParams();
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBidForm, setShowBidForm] = useState(false);
  const [bidForm, setBidForm] = useState({ labour_cost: '', materials_cost: '', tfare: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState({ stars: 0, comment: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [showCounter, setShowCounter] = useState(null);
  const [counterForm, setCounterForm] = useState({ amount: '', note: '' });
  const [showDispute, setShowDispute] = useState(false);
  const [disputeForm, setDisputeForm] = useState({ type: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const [newBidIds, setNewBidIds] = useState(new Set());

  const load = async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data.job);
      setBids(data.bids || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  // ── Live bidding updates via SSE ──
  const handleNewBid = useCallback((bid) => {
    setBids(prev => {
      if (prev.some(b => b.id === bid.id)) return prev;
      return [bid, ...prev];
    });
    setNewBidIds(prev => new Set([...prev, bid.id]));
    // Clear "new" highlight after 5s
    setTimeout(() => setNewBidIds(prev => { const next = new Set(prev); next.delete(bid.id); return next; }), 5000);
  }, []);

  const handleBidAccepted = useCallback((bidId) => {
    setBids(prev => prev.map(b => ({
      ...b,
      status: b.id === bidId ? 'accepted' : (b.status === 'pending' ? 'rejected' : b.status),
    })));
    setJob(prev => prev ? { ...prev, status: 'accepted', accepted_bid_id: bidId } : prev);
  }, []);

  const handleCounterOffer = useCallback(() => { load(); }, []);

  const { connected } = useLiveJob(id, {
    onNewBid: handleNewBid,
    onBidAccepted: handleBidAccepted,
    onCounterOffer: handleCounterOffer,
  });

  const handleBid = async () => {
    setSubmitting(true);
    try {
      await api.post('/bids', { job_id: id, labour_cost: parseFloat(bidForm.labour_cost) || 0, materials_cost: parseFloat(bidForm.materials_cost) || 0, tfare: parseFloat(bidForm.tfare) || 0, note: bidForm.note });
      setShowBidForm(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSubmitting(false); }
  };

  const acceptBid = async (bidId) => { try { await api.post(`/bids/${bidId}/accept`); load(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } };

  const submitCounter = async (bidId) => {
    try {
      await api.post(`/bids/${bidId}/counter`, { amount: parseFloat(counterForm.amount), note: counterForm.note });
      setShowCounter(null); setCounterForm({ amount: '', note: '' }); load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const submitRating = async (ratedUserId) => {
    if (rating.stars < 1) return;
    try { await api.post('/ratings', { job_id: id, rated_user_id: ratedUserId, stars: rating.stars, comment: rating.comment }); setShowRating(false); alert('Rating submitted!'); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const initPayment = async (type) => {
    setPayLoading(true);
    try {
      const email = user?.email || `${user?.phone}@naijafix.com`;
      const { data } = await api.post('/payments/initialize', { job_id: id, email, payment_type: type });
      window.location.href = data.authorization_url;
    } catch (err) { alert(err.response?.data?.detail || 'Payment failed'); setPayLoading(false); }
  };

  const submitDispute = async () => {
    try { await api.post('/disputes', { job_id: id, dispute_type: disputeForm.type, description: disputeForm.description }); setShowDispute(false); load(); alert('Dispute filed'); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const { data } = await api.post('/upload/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/jobs/${id}/photos`, [data.photo_id]);
      load();
    } catch (err) { alert('Upload failed'); } finally { setUploading(false); }
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>;
  if (!job) return <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center"><p className="text-[#5C5C5C]">Job not found</p></div>;

  const isOwner = user && job.customer_id === user.id;
  const isArtisan = user && (user.role === 'artisan' || user.role === 'facility_manager');
  const alreadyBid = bids.some(b => b.artisan_id === user?.id);
  const acceptedBid = bids.find(b => b.status === 'accepted');

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="job-detail-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1 truncate">{job.title}</h1>
      </header>

      <div className="p-4 space-y-4">
        {/* Job Info */}
        <div className="bg-white border border-[#D4C5B3] rounded-xl p-5">
          <div className="flex gap-2 mb-3">
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-[#1A4D2E]/5 text-[#1A4D2E]">{job.category?.replace(/_/g, ' ')}</span>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${job.status === 'disputed' ? 'bg-red-50 text-red-600' : 'bg-[#E86A33]/5 text-[#E86A33]'}`}>{job.status?.replace(/_/g, ' ')}</span>
            {job.booking_fee_paid && <span className="text-xs font-bold px-2 py-1 rounded-full bg-[#2D6A4F]/5 text-[#2D6A4F]">Fee Paid</span>}
          </div>
          <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-2">{job.title}</h2>
          <p className="text-sm text-[#5C5C5C] leading-relaxed mb-4 font-body">{job.description}</p>
          <div className="grid grid-cols-2 gap-3 text-sm text-[#5C5C5C]">
            <div className="flex items-center gap-2"><MapPin size={16} className="text-[#E86A33]" />{job.lga}, {job.state}</div>
            <div className="flex items-center gap-2"><Clock size={16} className="text-[#E86A33]" />{job.days_needed} day{job.days_needed > 1 ? 's' : ''}</div>
          </div>
          {/* Photos */}
          {job.photos?.length > 0 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {job.photos.map((p, i) => <div key={i} className="w-16 h-16 bg-[#E8DDD2] rounded-lg flex-shrink-0 flex items-center justify-center"><Image size={20} className="text-[#5C5C5C]" /></div>)}
            </div>
          )}
          {isOwner && job.status === 'open' && (
            <label className="mt-3 flex items-center gap-2 text-xs font-bold text-[#E86A33] cursor-pointer" data-testid="upload-photo-button">
              <Camera size={16} /> {uploading ? 'Uploading...' : 'Add Photo'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
            </label>
          )}
        </div>

        {/* AI Estimate */}
        {job.estimate && (
          <div className="bg-white border border-[#D4C5B3] rounded-xl overflow-hidden" data-testid="job-estimate">
            <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] px-4 py-2 text-white font-bold text-xs">AI Cost Estimate</div>
            <div className="p-4"><div className="font-heading text-lg font-bold text-[#E86A33]">{formatNaira(job.estimate.total_min)} – {formatNaira(job.estimate.total_max)}</div></div>
          </div>
        )}

        {/* Payment Buttons */}
        {isOwner && job.status === 'open' && !job.booking_fee_paid && (
          <button onClick={() => initPayment('booking_fee')} disabled={payLoading} className="w-full bg-[#E86A33] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 font-body disabled:opacity-50" data-testid="pay-booking-fee">
            <CurrencyDollar size={18} /> {payLoading ? 'Processing...' : 'Pay Booking Fee (₦3,500)'}
          </button>
        )}
        {isOwner && job.status === 'accepted' && (
          <button onClick={() => initPayment('escrow')} disabled={payLoading} className="w-full bg-[#2D6A4F] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 font-body disabled:opacity-50" data-testid="pay-escrow">
            <CurrencyDollar size={18} /> {payLoading ? 'Processing...' : 'Pay into Escrow'}
          </button>
        )}

        {/* Bids */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C]">Bids ({bids.length})</h3>
            {connected && (
              <span className="flex items-center gap-1.5 text-[10px] font-bold text-[#2D6A4F] bg-[#2D6A4F]/10 px-2.5 py-1 rounded-full" data-testid="live-indicator">
                <span className="w-1.5 h-1.5 bg-[#2D6A4F] rounded-full animate-pulse" />
                LIVE
              </span>
            )}
          </div>
          {bids.length === 0 ? (
            <div className="bg-white border border-[#D4C5B3] rounded-xl p-6 text-center"><p className="text-sm text-[#5C5C5C] font-body">No bids yet.</p></div>
          ) : (
            <div className="space-y-3">
              {bids.map(bid => (
                <div
                  key={bid.id}
                  className={`bg-white border rounded-xl p-4 transition-all duration-500 ${bid.status === 'accepted' ? 'border-[#2D6A4F] bg-[#2D6A4F]/5' : 'border-[#D4C5B3]'} ${newBidIds.has(bid.id) ? 'ring-2 ring-[#E86A33] animate-[slideIn_0.5s_ease-out] shadow-lg shadow-[#E86A33]/20' : ''}`}
                  data-testid={`bid-${bid.id}`}
                >
                  {newBidIds.has(bid.id) && (
                    <div className="flex items-center gap-1 mb-2 text-[10px] font-black text-[#E86A33] bg-[#E86A33]/10 px-2 py-1 rounded-full w-fit animate-pulse" data-testid="new-bid-badge">
                      <Lightning size={10} weight="fill" /> NEW BID
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-bold text-sm text-[#1C1C1C] font-body">{bid.artisan_name}</h4>
                      <span className="text-xs text-[#5C5C5C]">{bid.artisan_trade} {bid.artisan_role === 'facility_manager' ? '· FM' : ''}</span>
                      {isOwner && <a href={`/portfolios/${bid.artisan_id}`} className="block text-[10px] font-bold text-[#E86A33] mt-0.5 hover:underline" data-testid={`view-portfolio-${bid.id}`}>View Portfolio →</a>}
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-lg font-bold text-[#E86A33]">{formatNaira(bid.total)}</div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${bid.status === 'accepted' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : bid.status?.includes('counter') ? 'bg-[#F4A261]/10 text-[#F4A261]' : 'bg-blue-50 text-blue-600'}`}>{bid.status?.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                  {bid.note && <p className="text-xs text-[#5C5C5C] italic bg-[#E8DDD2] p-2 rounded-lg mb-2 font-body">{bid.note}</p>}
                  <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                    <div className="bg-[#E8DDD2] rounded-lg p-2 text-center"><span className="text-[#5C5C5C] block">Labour</span><span className="font-bold">{formatNaira(bid.labour_cost)}</span></div>
                    <div className="bg-[#E8DDD2] rounded-lg p-2 text-center"><span className="text-[#5C5C5C] block">Materials</span><span className="font-bold">{formatNaira(bid.materials_cost)}</span></div>
                    <div className="bg-[#E8DDD2] rounded-lg p-2 text-center"><span className="text-[#5C5C5C] block">Transport</span><span className="font-bold">{formatNaira(bid.tfare)}</span></div>
                  </div>
                  {/* Counter offer history */}
                  {bid.counter_offers?.length > 0 && (
                    <div className="bg-[#F4A261]/5 border border-[#F4A261]/20 rounded-lg p-3 mb-2">
                      <p className="text-[10px] font-bold text-[#F4A261] mb-1">NEGOTIATION HISTORY</p>
                      {bid.counter_offers.map((c, i) => (
                        <div key={i} className="text-xs text-[#5C5C5C] mb-1 font-body">
                          <span className="font-bold">{c.by_name || c.by_role}</span>: {formatNaira(c.amount)} {c.note && `— "${c.note}"`}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Action buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {isOwner && bid.status !== 'accepted' && bid.status !== 'rejected' && (
                      <>
                        <button onClick={() => acceptBid(bid.id)} className="flex-1 bg-[#2D6A4F] text-white font-bold py-2 rounded-lg text-xs font-body flex items-center justify-center gap-1" data-testid={`accept-bid-${bid.id}`}><Check size={14} /> Accept</button>
                        <button onClick={() => setShowCounter(showCounter === bid.id ? null : bid.id)} className="flex-1 bg-[#F4A261]/10 text-[#F4A261] font-bold py-2 rounded-lg text-xs font-body border border-[#F4A261]/30 flex items-center justify-center gap-1" data-testid={`counter-bid-${bid.id}`}><ArrowsCounterClockwise size={14} /> Counter</button>
                      </>
                    )}
                    {isArtisan && bid.artisan_id === user?.id && bid.status?.includes('counter_customer') && (
                      <button onClick={() => setShowCounter(showCounter === bid.id ? null : bid.id)} className="flex-1 bg-[#F4A261]/10 text-[#F4A261] font-bold py-2 rounded-lg text-xs font-body border border-[#F4A261]/30" data-testid={`respond-counter-${bid.id}`}>Respond to Counter</button>
                    )}
                  </div>
                  {/* Counter form */}
                  {showCounter === bid.id && (
                    <div className="mt-3 p-3 bg-[#F4A261]/5 border border-[#F4A261]/20 rounded-xl space-y-2" data-testid="counter-form">
                      <p className="text-xs font-bold text-[#F4A261]">Your Counter Offer</p>
                      <input data-testid="counter-amount" type="number" placeholder="Amount (₦)" value={counterForm.amount} onChange={e => setCounterForm(f => ({ ...f, amount: e.target.value }))} className="w-full bg-white border border-[#D4C5B3] rounded-lg px-3 py-2 text-sm font-body outline-none" />
                      <input data-testid="counter-note" placeholder="Note (optional)" value={counterForm.note} onChange={e => setCounterForm(f => ({ ...f, note: e.target.value }))} className="w-full bg-white border border-[#D4C5B3] rounded-lg px-3 py-2 text-sm font-body outline-none" />
                      <div className="flex gap-2">
                        <button onClick={() => setShowCounter(null)} className="flex-1 bg-[#E8DDD2] text-[#5C5C5C] font-bold py-2 rounded-lg text-xs">Cancel</button>
                        <button onClick={() => submitCounter(bid.id)} disabled={!counterForm.amount} className="flex-1 bg-[#F4A261] text-white font-bold py-2 rounded-lg text-xs disabled:opacity-50" data-testid="send-counter-button">Send Counter</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bid Form for Artisans */}
        {isArtisan && !alreadyBid && job.status === 'open' && (
          !showBidForm ? (
            <button onClick={() => setShowBidForm(true)} className="w-full bg-[#E86A33] text-white font-bold py-3.5 rounded-xl font-body" data-testid="open-bid-form">Place a Bid</button>
          ) : (
            <div className="bg-white border-2 border-[#E86A33] rounded-xl p-5" data-testid="bid-form">
              <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-3">Your Bid</h3>
              <div className="space-y-3">
                <div><label className="text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-1 block">Labour (₦)</label><input data-testid="bid-labour" type="number" value={bidForm.labour_cost} onChange={e => setBidForm(f => ({ ...f, labour_cost: e.target.value }))} placeholder="0" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none" /></div>
                <div><label className="text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-1 block">Materials (₦)</label><input data-testid="bid-materials" type="number" value={bidForm.materials_cost} onChange={e => setBidForm(f => ({ ...f, materials_cost: e.target.value }))} placeholder="0" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none" /></div>
                <div><label className="text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-1 block">Transport (₦)</label><input data-testid="bid-tfare" type="number" value={bidForm.tfare} onChange={e => setBidForm(f => ({ ...f, tfare: e.target.value }))} placeholder="0" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none" /></div>
                <div className="bg-[#E86A33]/5 border border-[#E86A33]/20 rounded-lg p-3 text-center">
                  <span className="text-xs text-[#5C5C5C]">Total</span>
                  <span className="block font-heading text-xl font-bold text-[#E86A33]">{formatNaira((parseFloat(bidForm.labour_cost) || 0) + (parseFloat(bidForm.materials_cost) || 0) + (parseFloat(bidForm.tfare) || 0))}</span>
                </div>
                <textarea data-testid="bid-note" value={bidForm.note} onChange={e => setBidForm(f => ({ ...f, note: e.target.value }))} rows={2} placeholder="Note to customer..." className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none resize-none" />
                <div className="flex gap-3">
                  <button onClick={() => setShowBidForm(false)} className="flex-1 bg-[#E8DDD2] text-[#5C5C5C] font-bold py-3 rounded-lg font-body">Cancel</button>
                  <button onClick={handleBid} disabled={submitting || !bidForm.labour_cost} className="flex-1 bg-[#E86A33] text-white font-bold py-3 rounded-lg font-body disabled:opacity-50" data-testid="submit-bid-button"><PaperPlaneTilt size={16} className="inline mr-1" />{submitting ? 'Sending...' : 'Submit'}</button>
                </div>
              </div>
            </div>
          )
        )}
        {isArtisan && alreadyBid && <div className="bg-[#2D6A4F]/10 border border-[#2D6A4F]/20 rounded-xl p-4 text-center text-sm font-bold text-[#2D6A4F]">You've already bid on this job</div>}

        {/* Chat Button */}
        {acceptedBid && (
          <button onClick={() => navigate(`/chat/${id}/${isOwner ? acceptedBid.artisan_id : job.customer_id}`)} className="w-full bg-white border-2 border-[#1A4D2E] text-[#1A4D2E] font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body" data-testid="open-chat">
            <ChatCircle size={18} weight="duotone" /> Chat
          </button>
        )}

        {/* Milestones Button */}
        {acceptedBid && ['accepted', 'in_progress'].includes(job.status) && (
          <button onClick={() => navigate(`/milestones/${id}`)} className="w-full bg-white border-2 border-[#E86A33] text-[#E86A33] font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body" data-testid="open-milestones">
            <ListChecks size={18} weight="duotone" /> Milestones
          </button>
        )}

        {/* Progress Tracker */}
        <button onClick={() => navigate(`/progress/${id}`)} className="w-full bg-white border-2 border-[#2D6A4F] text-[#2D6A4F] font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body" data-testid="open-progress">
          <Path size={18} weight="duotone" /> Track Progress
        </button>

        {/* Rating */}
        {isOwner && job.status === 'completed' && !showRating && (
          <button onClick={() => setShowRating(true)} className="w-full bg-[#F4A261] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body" data-testid="open-rating"><Star size={18} weight="fill" /> Rate Artisan</button>
        )}
        {showRating && (
          <div className="bg-white border-2 border-[#F4A261] rounded-xl p-5" data-testid="rating-form">
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-3 text-center">Rate the Artisan</h3>
            <div className="flex justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map(s => (
                <button key={s} onClick={() => setRating(r => ({ ...r, stars: s }))} className="transition-transform hover:scale-110" data-testid={`star-${s}`}>
                  {s <= rating.stars ? <Star size={36} weight="fill" className="text-[#F4A261]" /> : <Star size={36} className="text-[#E5E3DB]" />}
                </button>
              ))}
            </div>
            <textarea data-testid="rating-comment" value={rating.comment} onChange={e => setRating(r => ({ ...r, comment: e.target.value }))} placeholder="Leave a comment..." rows={3} className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none resize-none mb-3" />
            <div className="flex gap-3">
              <button onClick={() => setShowRating(false)} className="flex-1 bg-[#E8DDD2] text-[#5C5C5C] font-bold py-3 rounded-lg font-body">Cancel</button>
              <button onClick={() => submitRating(acceptedBid?.artisan_id)} disabled={rating.stars < 1} className="flex-1 bg-[#F4A261] text-white font-bold py-3 rounded-lg font-body disabled:opacity-50" data-testid="submit-rating-button">Submit</button>
            </div>
          </div>
        )}

        {/* Dispute Button */}
        {(isOwner || isArtisan) && ['in_progress', 'accepted', 'completed'].includes(job.status) && !showDispute && (
          <button onClick={() => setShowDispute(true)} className="w-full bg-red-50 border border-red-200 text-red-600 font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body text-sm" data-testid="open-dispute">
            <WarningOctagon size={16} /> File a Dispute
          </button>
        )}
        {showDispute && (
          <div className="bg-white border-2 border-red-300 rounded-xl p-5" data-testid="dispute-form">
            <h3 className="font-heading text-lg font-bold text-red-600 mb-3">File a Dispute</h3>
            <p className="text-xs text-[#5C5C5C] mb-3 font-body">Mediation fee: ₦15,000 (charged to losing party)</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {['incomplete_work', 'poor_quality', 'overcharged', 'no_show'].map(t => (
                <button key={t} onClick={() => setDisputeForm(f => ({ ...f, type: t }))} data-testid={`dispute-type-${t}`}
                  className={`p-2 rounded-lg border text-xs font-bold text-center ${disputeForm.type === t ? 'border-red-400 bg-red-50 text-red-600' : 'border-[#D4C5B3] text-[#5C5C5C]'}`}>
                  {t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </button>
              ))}
            </div>
            <textarea data-testid="dispute-description" value={disputeForm.description} onChange={e => setDisputeForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue..." rows={3} className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none resize-none mb-3" />
            <div className="flex gap-3">
              <button onClick={() => setShowDispute(false)} className="flex-1 bg-[#E8DDD2] text-[#5C5C5C] font-bold py-3 rounded-lg font-body">Cancel</button>
              <button onClick={submitDispute} disabled={!disputeForm.type || !disputeForm.description} className="flex-1 bg-red-500 text-white font-bold py-3 rounded-lg font-body disabled:opacity-50" data-testid="submit-dispute-button">Submit Dispute</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
